/**
 * Tela de redefinição de senha via token (e-mail).
 * Layout split idêntico ao OtpPage: coluna branca à esquerda + painel metálico dourado à direita.
 */
import { FormEvent, useState } from 'react';
import axios from 'axios';
import { PiArrowLeftBold, PiEyeBold, PiEyeSlashBold, PiSpinnerBold } from 'react-icons/pi';
import { cn } from '@v2/utils/cn';

const authApi = axios.create({
    withCredentials: true,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
});
const csrf = (window as any).__AUTH_PAGE__?.csrfToken ?? '';
if (csrf) authApi.defaults.headers.common['X-CSRF-TOKEN'] = csrf;

/* ─── Linha decorativa dourada ─────────────────────────────── */
function GoldDivider() {
    return (
        <div
            className="h-px w-24"
            style={{ background: 'linear-gradient(90deg, transparent 0%, #C9A84C 50%, transparent 100%)' }}
        />
    );
}

/* ─── Campo com toggle de visibilidade ─────────────────────── */
function PasswordField({
    id, label, value, onChange, error, autoComplete,
}: {
    id: string; label: string; value: string;
    onChange: (v: string) => void; error?: string; autoComplete?: string;
}) {
    const [show, setShow] = useState(false);

    return (
        <div>
            <label htmlFor={id} className="mb-1.5 block text-sm font-semibold text-gray-700">
                {label}
            </label>
            <div className="relative">
                <input
                    id={id}
                    type={show ? 'text' : 'password'}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    autoComplete={autoComplete}
                    className={cn(
                        'w-full rounded-xl border px-4 py-3 pr-11 text-sm outline-none transition',
                        'border-gray-200 bg-white text-gray-900 placeholder-gray-400',
                        'focus:border-[#C9A84C] focus:ring-2 focus:ring-[#C9A84C]/20',
                        error && 'border-red-400 focus:border-red-400 focus:ring-red-100',
                    )}
                />
                <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                >
                    {show ? <PiEyeSlashBold className="size-5" /> : <PiEyeBold className="size-5" />}
                </button>
            </div>
            {error && <p className="mt-1.5 text-xs text-red-500">{error}</p>}
        </div>
    );
}

type Props = {
    token: string;
    email: string;
    onBack: () => void;
};

type Status = 'idle' | 'loading' | 'success' | 'invalid_token';

export default function ResetPasswordPage({ token, email, onBack }: Props) {
    const [password, setPassword]       = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [status, setStatus]           = useState<Status>('idle');
    const [errors, setErrors]           = useState<{ password?: string; general?: string }>({});

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setErrors({});

        if (password.length < 8) {
            setErrors({ password: 'A senha deve ter no mínimo 8 caracteres.' });
            return;
        }
        if (password !== confirmation) {
            setErrors({ password: 'As senhas não coincidem.' });
            return;
        }
        if (!token || !email) {
            setErrors({ general: 'Link inválido. Solicite um novo link de redefinição.' });
            return;
        }

        setStatus('loading');
        try {
            const { data } = await authApi.post('/login/reset-password', {
                token,
                email,
                password,
                password_confirmation: confirmation,
            });
            setStatus('success');
            setTimeout(() => { window.location.href = data.redirect ?? '/'; }, 1200);
        } catch (err: any) {
            const message: string = err?.response?.data?.message ?? 'Erro ao redefinir a senha.';
            const isInvalidToken  = message.includes('inválido') || message.includes('expirado')
                || message.includes('invalid') || message.includes('expired')
                || err?.response?.status === 422;

            if (isInvalidToken && message !== 'The password field confirmation does not match.') {
                setStatus('invalid_token');
            } else {
                setStatus('idle');
                setErrors({ general: message });
            }
        }
    };

    /* ── Estado: token inválido/expirado ──────────────────── */
    if (status === 'invalid_token') {
        return (
            <div className="flex min-h-screen">
                <div className="flex w-full flex-col justify-between px-6 py-10 sm:px-10 lg:w-[48%] lg:px-16 xl:px-24">
                    <div>
                        <img src="/img/design/Logo_Buckler_Oldblack.png" alt="Buckler Fit" className="h-8" />
                    </div>
                    <div className="mx-auto w-full max-w-md text-center">
                        <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl bg-red-50">
                            <span className="text-2xl">⚠️</span>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">Link expirado ou inválido</h2>
                        <p className="mt-2 text-sm text-gray-500">
                            O link de redefinição expirou ou já foi utilizado.
                            Solicite um novo link na tela de login.
                        </p>
                        <button
                            type="button"
                            onClick={onBack}
                            className="btn-gold mt-6 inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm"
                        >
                            <PiArrowLeftBold className="size-4" /> Voltar ao login
                        </button>
                    </div>
                    <p className="text-center text-xs text-gray-400">
                        © {new Date().getFullYear()} Buckler Fit · Gestão de Importações
                    </p>
                </div>
                <RightPanel />
            </div>
        );
    }

    /* ── Estado: senha redefinida com sucesso ─────────────── */
    if (status === 'success') {
        return (
            <div className="flex min-h-screen">
                <div className="flex w-full flex-col justify-between px-6 py-10 sm:px-10 lg:w-[48%] lg:px-16 xl:px-24">
                    <div>
                        <img src="/img/design/Logo_Buckler_Oldblack.png" alt="Buckler Fit" className="h-8" />
                    </div>
                    <div className="mx-auto w-full max-w-md text-center">
                        <div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-2xl bg-[#FDF5DC]">
                            <span className="text-2xl">✅</span>
                        </div>
                        <h2 className="text-xl font-bold text-gray-900">Senha redefinida!</h2>
                        <p className="mt-2 text-sm text-gray-500">
                            Sua senha foi atualizada. Redirecionando para o painel…
                        </p>
                        <div className="mt-4 flex justify-center">
                            <PiSpinnerBold className="size-5 animate-spin text-[#C9A84C]" />
                        </div>
                    </div>
                    <p className="text-center text-xs text-gray-400">
                        © {new Date().getFullYear()} Buckler Fit · Gestão de Importações
                    </p>
                </div>
                <RightPanel />
            </div>
        );
    }

    /* ── Formulário principal ─────────────────────────────── */
    return (
        <div className="flex min-h-screen">

            {/* ── Coluna esquerda: formulário ──────────────── */}
            <div className="flex w-full flex-col justify-between px-6 py-10 sm:px-10 lg:w-[48%] lg:px-16 xl:px-24">
                {/* Logo */}
                <div>
                    <img src="/img/design/Logo_Buckler_Oldblack.png" alt="Buckler Fit" className="h-8" />
                </div>

                {/* Formulário */}
                <div className="mx-auto w-full max-w-md">
                    {/* Voltar */}
                    <button
                        type="button"
                        onClick={onBack}
                        className="mb-6 flex items-center gap-1.5 text-sm font-medium text-[#9A7B28] hover:text-[#C9A84C] transition-colors"
                    >
                        <PiArrowLeftBold className="size-4" /> Voltar ao login
                    </button>

                    <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">Redefinir senha</h2>
                    <p className="mt-1.5 text-sm text-gray-500">
                        Escolha uma senha forte com pelo menos{' '}
                        <span className="font-semibold text-[#9A7B28]">8 caracteres</span>.
                    </p>

                    <form onSubmit={handleSubmit} className="mt-8 space-y-5">
                        <PasswordField
                            id="password"
                            label="Nova senha"
                            value={password}
                            onChange={setPassword}
                            error={errors.password}
                            autoComplete="new-password"
                        />
                        <PasswordField
                            id="password_confirmation"
                            label="Confirmar nova senha"
                            value={confirmation}
                            onChange={setConfirmation}
                            autoComplete="new-password"
                        />

                        {errors.general && (
                            <p className="text-center text-sm font-medium text-red-500">{errors.general}</p>
                        )}

                        <button
                            type="submit"
                            disabled={status === 'loading'}
                            className="btn-gold mt-2 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm"
                        >
                            {status === 'loading' && <PiSpinnerBold className="size-4 animate-spin" />}
                            {status === 'loading' ? 'Redefinindo…' : 'Redefinir senha'}
                        </button>
                    </form>
                </div>

                {/* Rodapé */}
                <p className="text-center text-xs text-gray-400">
                    © {new Date().getFullYear()} Buckler Fit · Gestão de Importações
                </p>
            </div>

            {/* ── Coluna direita: painel metálico escuro ──── */}
            <RightPanel />
        </div>
    );
}

/* ─── Painel metálico dourado (reutilizado nos estados) ───── */
function RightPanel() {
    return (
        <div
            className="relative hidden flex-col items-center justify-center overflow-hidden lg:flex lg:w-[52%]"
            style={{ background: 'linear-gradient(160deg, #0f0d08 0%, #1a1508 40%, #0f0d08 100%)' }}
        >
            <div className="pointer-events-none absolute inset-0">
                <div
                    className="absolute left-1/2 top-1/2 size-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-20 blur-3xl"
                    style={{ background: 'radial-gradient(circle, #C9A84C 0%, transparent 70%)' }}
                />
                <div className="absolute -left-24 bottom-0 size-72 rounded-full opacity-10 blur-2xl" style={{ background: '#D4AF37' }} />
                <div className="absolute -right-24 top-0 size-72 rounded-full opacity-10 blur-2xl" style={{ background: '#9A7B28' }} />
            </div>
            <div className="absolute left-0 top-0 h-full w-px"
                style={{ background: 'linear-gradient(180deg, transparent 0%, #C9A84C 30%, #E8CC6A 50%, #C9A84C 70%, transparent 100%)' }} />

            <div className="relative z-10 flex flex-col items-center px-12 text-center">
                <img src="/img/design/Logo_Buckler_OffWhite.png" alt="Buckler Fit" className="mb-10 h-12 opacity-90" />
                <GoldDivider />
                <blockquote className="mt-8 max-w-sm">
                    <p className="text-2xl font-bold leading-snug" style={{ color: '#E8CC6A' }}>
                        "Queira mais que os outros."
                    </p>
                    <footer className="mt-6">
                        <p className="text-sm font-bold text-white/80">Bruno Parlato</p>
                        <p className="mt-1 text-xs italic text-white/35">
                            "Deve ser ruim p*ra car*lho não ser Buckler."
                        </p>
                    </footer>
                </blockquote>
                <GoldDivider />
            </div>
        </div>
    );
}
